import { useMutation, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/shared/hooks/queries/queryKeys'
import {
  postAudio,
  patchYakurekiEvent,
  AudioSource,
} from '@/features/yakureki/apis/audio'
import { useToast } from '@/shared/contexts/ToastContext'
import { executeSuperReload } from '@/shared/helpers/superReload'

// ========================================
// Types
// ========================================

interface PostAudioVariables {
  audioSource: AudioSource
  label: string
  pharmacistId?: string
  memoItems?: string[]
}

interface PatchYakurekiEventVariables {
  audioSource: AudioSource
  label: string
  yakurekiId: string
  pharmacistId?: string
  memoItems?: string[]
  /** 同伴/代理の別 (log/207 batch 経路). undefined = 変更しない. */
  hasCompanionOrSubstitute?: boolean
}

// ========================================
// 共通エラーハンドラー
// ========================================

type ShowToastFn = (
  message: string,
  severity: 'success' | 'error' | 'warning' | 'info',
  duration?: number
) => void

/**
 * 音声アップロードの共通エラーハンドリング
 */
const handleAudioUploadError = (error: unknown, showToast: ShowToastFn) => {
  const axiosError = error as {
    response?: { status?: number; data?: { error?: string } }
  }
  const status = axiosError.response?.status
  const errorMessage = axiosError.response?.data?.error

  if (status === 400) {
    showToast(errorMessage || '音声データが無効です', 'error', 5000)
  } else if (status === 403) {
    showToast('この操作を行う権限がありません', 'error', 5000)
  } else if (status === 404) {
    showToast('対象のデータが見つかりません', 'error', 5000)
  } else if (!navigator.onLine) {
    showToast('ネットワーク接続を確認してください', 'error', 5000)
  } else {
    showToast('音声のアップロードに失敗しました', 'error', 5000)
  }
}

// ========================================
// usePostAudioMutation
// ========================================

/**
 * 新規薬歴音声アップロードMutation（TanStack Query）
 *
 * 責務:
 * - 新規音声データのアップロード（録音URLまたはファイル）
 * - エラーハンドリング（400, 403, 404, オフライン）
 * - 成功時に患者リストをフルリロードし、status反映を保証
 *
 * mutateAsync 完了時点で患者リストは最新状態になっている。
 * 呼び出し元で別途リロードする必要はない。
 */
export const usePostAudioMutation = () => {
  const queryClient = useQueryClient()
  const { showToast } = useToast()

  return useMutation({
    mutationFn: async ({
      audioSource,
      label,
      pharmacistId,
      memoItems,
    }: PostAudioVariables) => {
      await postAudio(audioSource, label, pharmacistId, memoItems)
      // 患者リストをフルリロードして status=processing の反映を保証
      await executeSuperReload()
      await queryClient.invalidateQueries({
        queryKey: queryKeys.patients.all,
      })
    },
    onError: (error) => handleAudioUploadError(error, showToast),
  })
}

// ========================================
// usePatchYakurekiEventMutation
// ========================================

/**
 * 既存薬歴音声更新Mutation（TanStack Query）
 *
 * 責務:
 * - 既存薬歴の音声データ更新（録音URLまたはファイル）
 * - エラーハンドリング（400, 403, 404, オフライン）
 * - 成功時に患者リスト・薬歴詳細をフルリロードし、status反映を保証
 *
 * mutateAsync 完了時点で患者リスト・薬歴詳細は最新状態になっている。
 * 呼び出し元で別途リロードする必要はない。
 */
export const usePatchYakurekiEventMutation = () => {
  const queryClient = useQueryClient()
  const { showToast } = useToast()

  return useMutation({
    mutationFn: async ({
      audioSource,
      label,
      yakurekiId,
      pharmacistId,
      memoItems,
      hasCompanionOrSubstitute,
    }: PatchYakurekiEventVariables) => {
      await patchYakurekiEvent(
        audioSource,
        label,
        yakurekiId,
        pharmacistId,
        memoItems,
        hasCompanionOrSubstitute
      )
      // 患者リストをフルリロードして status=processing の反映を保証
      await executeSuperReload()
      await queryClient.invalidateQueries({
        queryKey: queryKeys.patients.all,
      })
      await queryClient.invalidateQueries({
        queryKey: queryKeys.yakureki.detail(yakurekiId),
      })
    },
    onError: (error) => handleAudioUploadError(error, showToast),
  })
}
